import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosButton } from "mosaic-react";
import { AutoAwesome, CheckCircle } from "mosaic-react-icons";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks, hasModule } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    getImportProfileByIdAction,
    getAvailableMappingContextsAction,
    getAvailableEntitiesAction,
    getAvailableCdmFieldsAction,
    createImportProfileMappingAction,
    getImportProfileMappingByProfileAction,
    suggestCustomTemplateEntityAction,
    suggestCustomTemplateMappingAction,
    updateImportProfileMappingAction,
    runCustomTemplateMappingAction,
    getImportProfileIssuesAction,
    fixImportProfileCellAction,
    listStagingTranslationsAction,
    createStagingTranslationAction,
    updateStagingTranslationAction,
    toggleStagingTranslationStateAction,
    softDeleteStagingTranslationAction,
    applyStagingTranslationAction,
    suggestDataFixesAction,
    acceptDataFixesAction,
} from "../../store/actions";
import ColumnMappingCanvas from "../../store/views/MappingFlow/ColumnMappingCanvas";
import StagingDataPanel from "../../store/views/MappingFlow/StagingDataPanel/StagingDataPanel";
import { filteredQueriesByModules } from "../../routes/manageRouting";
import Header from "./ColumnMappingFlowHeader";
import { mapProperties } from "colbi_web_ui/lib/utils";
import { checkRolesByOrganizationProject } from "colbi_web_ui/lib/utils/roleValidator";
import { UserRoles } from "../../enums/userRoles";

const ColumnMappingFlow = (props) => {
    const dispatch = useDispatch();
    const [canvasStep, setCanvasStep] = useState(null);
    const [canvasImported, setCanvasImported] = useState(false);
    const [mappingPersisted, setMappingPersisted] = useState(false);
    const [mappingLoading, setMappingLoading] = useState(true);
    const [canvasActions, setCanvasActions] = useState({});
    const [importProfileName, setImportProfileName] = useState("");
    // Column names that had at least one non-empty value across every file
    // ever uploaded for this profile (from profile_headers metadata). Columns
    // missing from this set have never had data, so they default to "Do not
    // import" instead of forcing the user to skip them one by one.
    const [columnsWithData, setColumnsWithData] = useState(null);
    // Allows deep-linking straight into the Staging Data tab (e.g. from the
    // Import Profiles grid, when a profile already has a mapping) via ?tab=staging.
    const [activeTab, setActiveTab] = useState(() =>
        new URLSearchParams(props.location?.search || "").get("tab") === "staging"
            ? "staging"
            : "mapping",
    );
    const [savedMapping, setSavedMapping] = useState(null);

    const handleStepChange = useCallback(({
        step,
        imported,
        hasPersistedMapping,
        mappingLoading: isMappingLoading,
    }) => {
        setCanvasStep(step);
        setCanvasImported(imported);
        setMappingPersisted(Boolean(hasPersistedMapping));
        setMappingLoading(Boolean(isMappingLoading));
    }, []);

    const handleActionStateChange = useCallback((actions) => {
        setCanvasActions(actions);
    }, []);

    const handleMappingSaved = useCallback((info) => {
        setSavedMapping(info);
    }, []);
    const dispatchGetAvailableMappingContexts = useCallback(
        (currentProjectId) => dispatch(getAvailableMappingContextsAction(currentProjectId)),
        [dispatch],
    );
    const dispatchGetImportProfileById = useCallback(
        (currentProjectId, currentProfileId) =>
            dispatch(getImportProfileByIdAction(currentProjectId, currentProfileId)),
        [dispatch],
    );
    const dispatchGetAvailableEntities = useCallback(
        (configSet, legalStructure) =>
            dispatch(getAvailableEntitiesAction(configSet, legalStructure)),
        [dispatch],
    );
    const dispatchGetAvailableCdmFields = useCallback(
        (configSet, entityName, fileType) =>
            dispatch(getAvailableCdmFieldsAction(configSet, entityName, fileType)),
        [dispatch],
    );
    const dispatchCreateImportProfileMapping = useCallback(
        (payload) => dispatch(createImportProfileMappingAction(payload)),
        [dispatch],
    );
    const dispatchGetImportProfileMappingByProfile = useCallback(
        (profileId) => dispatch(getImportProfileMappingByProfileAction(profileId)),
        [dispatch],
    );
    const dispatchUpdateImportProfileMapping = useCallback(
        (mappingId, projectIdArg, data) =>
            dispatch(updateImportProfileMappingAction(mappingId, projectIdArg, data)),
        [dispatch],
    );
    const dispatchListStagingTranslations = useCallback(
        (currentProfileId, currentProjectId) =>
            dispatch(listStagingTranslationsAction(currentProfileId, currentProjectId)),
        [dispatch],
    );
    const dispatchCreateStagingTranslation = useCallback(
        (payload) => dispatch(createStagingTranslationAction(payload)),
        [dispatch],
    );
    const dispatchUpdateStagingTranslation = useCallback(
        (translationId, payload) =>
            dispatch(updateStagingTranslationAction(translationId, payload)),
        [dispatch],
    );
    const dispatchToggleStagingTranslationState = useCallback(
        (translationId, state) =>
            dispatch(toggleStagingTranslationStateAction(translationId, state)),
        [dispatch],
    );
    const dispatchSoftDeleteStagingTranslation = useCallback(
        (translationId) => dispatch(softDeleteStagingTranslationAction(translationId)),
        [dispatch],
    );
    const dispatchApplyStagingTranslation = useCallback(
        (translationId, currentProfileId, currentProjectId) =>
            dispatch(
                applyStagingTranslationAction(translationId, currentProfileId, currentProjectId),
            ),
        [dispatch],
    );
    const dispatchRunCustomTemplateMapping = useCallback(
        (payload) => dispatch(runCustomTemplateMappingAction(payload)),
        [dispatch],
    );
    const {
        modules,
        route,
        location,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
        pushModal,
        reload,
    } = useControllerInitialization(props);

    let { fiscalYear, importId, entityRawData } = matchParams;
    const profileId = parseInt(entityRawData, 10) || undefined;
    const hasAiSuggestions = hasModule(modules, "ai_suggestions");

    useEffect(() => {
        let isActive = true;

        if (!projectId || !profileId) {
            setImportProfileName("");
            setColumnsWithData(null);
            return () => {
                isActive = false;
            };
        }

        setImportProfileName("");
        setColumnsWithData(null);
        dispatchGetImportProfileById(projectId, profileId).then((profile) => {
            if (!isActive) return;
            if (String(profile?.id ?? "") !== String(profileId)) return;
            setImportProfileName(profile?.profile_name || "");
            // null (not an empty Set) means "no signal yet" — e.g. metadata
            // parsing hasn't finished — so the canvas knows to skip the
            // empty-column default rather than risk marking real data columns
            // as "Do not import".
            setColumnsWithData(
                profile?.profile_headers
                    ? new Set(profile.profile_headers.split(",").filter(Boolean))
                    : null,
            );
        });

        return () => {
            isActive = false;
        };
    }, [dispatchGetImportProfileById, profileId, projectId]);
    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const dispatchSuggestCustomTemplateEntity = useCallback(
        (profileId, configSet, availableEntities) =>
            dispatch(
                suggestCustomTemplateEntityAction(
                    profileId,
                    configSet,
                    availableEntities,
                    organizationProjects?.id?.toString() || "",
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchSuggestCustomTemplateMapping = useCallback(
        (profileId, configSet, entityName, conversationId) =>
            dispatch(
                suggestCustomTemplateMappingAction(
                    profileId,
                    configSet,
                    entityName,
                    organizationProjects?.id?.toString() || "",
                    conversationId,
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchGetImportProfileIssues = useCallback(
        (profileId, mappingId, configSet, entityName, projectId) =>
            dispatch(
                getImportProfileIssuesAction(
                    profileId,
                    mappingId,
                    configSet,
                    entityName,
                    projectId,
                ),
            ),
        [dispatch],
    );
    const dispatchFixImportProfileCell = useCallback(
        (profileId, projectId, clbId, column, value) =>
            dispatch(fixImportProfileCellAction(profileId, projectId, clbId, column, value)),
        [dispatch],
    );
    const dispatchSuggestDataFixes = useCallback(
        (currentProfileId, currentProjectId, columnNames) =>
            dispatch(
                suggestDataFixesAction(
                    currentProfileId,
                    currentProjectId,
                    organizationProjects?.id?.toString() || "",
                    columnNames,
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchAcceptDataFixes = useCallback(
        (currentProfileId, currentProjectId, fixes) =>
            dispatch(acceptDataFixesAction(currentProfileId, currentProjectId, fixes)),
        [dispatch],
    );
    const { loading = true, success = false } = queryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "ColumnMappingFlow");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const { items, headerItems, metadata, filters, rawDataEdit } = mapProperties(
        {
            items: "query(0).items",
            headerItems: "query(0).headerItems",
            metadata: "query(0).metadata",
            filters: "query(0).filters",
            rawDataEdit: "query(0).rawDataEdit",
        },
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
        repositoryStatus: { status: 1 },
    };

    if (loading) {
        return LoadingComponent;
    }

    const {
        step: s,
        imported: imp,
        allMapped,
        savingMapping,
        loadingAiMapping,
        selectedEntityName,
        onCancel,
        onChangeEntity,
        onNext,
        onSuggestMapping: onSuggestMappingAction,
    } = canvasActions;

    const canMutateTranslations = checkRolesByOrganizationProject(userInformation, projectId, [
        UserRoles.ADMIN,
        UserRoles.OWNER,
        UserRoles.PP_SUPPORT,
        UserRoles.SUPPORT,
    ]);
    const nonOwnerTooltip = i18n`Only the project owner can manage translations.`;

    const handleProceedToMapping = () => {
        setActiveTab("mapping");
    };

    const showStagingDataTab = !!profileId;

    const showMappingActions = activeTab === "mapping";
    const headerActions = showMappingActions
        ? [
              imp ? null : s === 2 && onChangeEntity ? (
                  <SovosButton key="change-entity" variant="text" onClick={onChangeEntity}>
                      {i18n`Change entity`}
                  </SovosButton>
              ) : null,
              imp ? null : s === 2 && onCancel ? (
                  <SovosButton key="cancel" variant="text" onClick={onCancel}>
                      {i18n`Cancel`}
                  </SovosButton>
              ) : null,
              imp ? null : s === 2 && onSuggestMappingAction && selectedEntityName ? (
                  <SovosButton
                      key="ai"
                      variant="outlined"
                      size="small"
                      startIcon={
                          loadingAiMapping ? (
                              <CircularProgress size={14} sx={{ color: "inherit" }} />
                          ) : (
                              <AutoAwesome sx={{ fontSize: 14 }} />
                          )
                      }
                      onClick={onSuggestMappingAction}
                      disabled={loadingAiMapping}
                  >
                      {i18n`Map by AI`}
                  </SovosButton>
              ) : null,
              imp ? null : s === 2 ? (
                  <SovosButton
                      key="next"
                      variant="contained"
                      disabled={!allMapped || savingMapping}
                      onClick={onNext}
                      startIcon={<CheckCircle fontSize="small" />}
                  >
                      {savingMapping ? i18n`Saving...` : i18n`Save mapping`}
                  </SovosButton>
              ) : null,
          ].filter(Boolean)
        : [];

    return (
        <GlobalPageLayout
            globalLayoutProps={globalLayoutProps}
            modules={modules}
            headerProps={{
                fiscalYears: undefined,
                importIds: undefined,
                downloadableQueries,
                title: "",
                headerComponents,
                customHeader: (
                    <Header
                        step={canvasStep}
                        frameworkName={importProfileName}
                        imported={canvasImported}
                        mappingPersisted={mappingPersisted}
                        mappingLoading={mappingLoading}
                        actions={headerActions}
                        onStepClick={(stepNum) => canvasActions.onStepClick?.(stepNum)}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        showStagingDataTab={showStagingDataTab}
                        i18n={i18n}
                    />
                ),
            }}
            headerComponentsMapper={() => (headerComponentProperties) => (
                <headerComponentProperties.Component
                    key="column-mapping"
                    pushModal={pushModal}
                    i18n={i18n}
                    reload={reload}
                    projectId={projectId}
                    user={userInformation}
                    {...headerComponentProperties.props}
                />
            )}
            fetch={props.fetch}
            route={route}
            matchParams={matchParams}
            menu={props.menu}
            isLoadingContent={() => false}
        >
            <SovosBox sx={{ display: activeTab === "mapping" ? "block" : "none" }}>
                <ColumnMappingCanvas
                    isActive={activeTab === "mapping"}
                    items={items}
                    headerItems={headerItems}
                    metadata={metadata}
                    filters={filters}
                    rawDataEdit={rawDataEdit}
                    columnsWithData={columnsWithData}
                    profileId={profileId}
                    projectId={projectId}
                    fiscalYear={fiscalYear}
                    importId={parseInt(importId, 10) || undefined}
                    onFetchMappingContexts={dispatchGetAvailableMappingContexts}
                    onFetchEntities={dispatchGetAvailableEntities}
                    onFetchCdmFields={dispatchGetAvailableCdmFields}
                    onFetchExistingMapping={dispatchGetImportProfileMappingByProfile}
                    onCreateMapping={dispatchCreateImportProfileMapping}
                    onUpdateMapping={dispatchUpdateImportProfileMapping}
                    onRunMapping={dispatchRunCustomTemplateMapping}
                    onSuggestEntity={
                        hasAiSuggestions ? dispatchSuggestCustomTemplateEntity : undefined
                    }
                    onSuggestMapping={
                        hasAiSuggestions ? dispatchSuggestCustomTemplateMapping : undefined
                    }
                    onStepChange={handleStepChange}
                    onActionStateChange={handleActionStateChange}
                    onMappingSaved={handleMappingSaved}
                />
            </SovosBox>
            {activeTab === "staging" && showStagingDataTab && (
                <StagingDataPanel
                    key={`${profileId}:${savedMapping?.mappingId || "unsaved"}`}
                    profileId={profileId}
                    projectId={projectId}
                    mappingId={savedMapping?.mappingId}
                    configSet={savedMapping?.configSet}
                    entityName={savedMapping?.entityName}
                    items={items}
                    headerItems={headerItems}
                    metadata={metadata}
                    canMutateTranslations={canMutateTranslations}
                    nonOwnerTooltip={nonOwnerTooltip}
                    aiSuggestionsEnabled={hasAiSuggestions}
                    onFetchIssues={dispatchGetImportProfileIssues}
                    onFixCell={dispatchFixImportProfileCell}
                    onSuggestFixes={hasAiSuggestions ? dispatchSuggestDataFixes : undefined}
                    onBulkAcceptFixes={hasAiSuggestions ? dispatchAcceptDataFixes : undefined}
                    onProceed={handleProceedToMapping}
                    proceedLabel={i18n`Back to mapping`}
                    onListStagingTranslations={dispatchListStagingTranslations}
                    onCreateStagingTranslation={dispatchCreateStagingTranslation}
                    onUpdateStagingTranslation={dispatchUpdateStagingTranslation}
                    onToggleStagingTranslationState={dispatchToggleStagingTranslationState}
                    onSoftDeleteStagingTranslation={dispatchSoftDeleteStagingTranslation}
                    onApplyStagingTranslation={dispatchApplyStagingTranslation}
                    onStagingDataChanged={reload}
                />
            )}
        </GlobalPageLayout>
    );
};

ColumnMappingFlow.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "custom_csv_raw_data",
            args: {
                entity: args.entityRawData || "",
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
            },
            body: "items,headerItems,metadata{total},rawDataEdit",
            isDatagridReq: true,
        },
    ];
    return [...allQueries, ...deepCopyQueries];
};

export default connect(({ user, fetch, menu }) => ({ user, fetch, menu }), {
    logout,
    pushModal,
    popModal,
    removeModal,
    toggleMenu,
    setLocale,
    pushDialog,
})(withRouter(ColumnMappingFlow));
